import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_URL } from "..";
import Swal from "sweetalert2";
import Select from "react-select";
import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import Quill from "quill";
import Switch from "react-switch";
import "quill/dist/quill.snow.css";
import { Icon } from "@iconify/react";
import {
  GoogleMap,
  Marker,
  Circle,
  useLoadScript,
} from "@react-google-maps/api";

const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ";

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 1,
};

function formatDateString(dateStr) {
  if (!dateStr) return "N/A";

  const date = new Date(dateStr);

  if (Number.isNaN(date.getTime())) return "N/A";

  const options = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };

  return date.toLocaleDateString("en-GB", options);
}

const QuoteListLayer = ({
  jobsData,
  pagination = DEFAULT_PAGINATION,
  onPageChange,
  onLimitChange,
  loading,
  searchTerm,
  searchColumn = "all",
  onSearchColumnChange,
  onSearchTermChange,
  onSearchSubmit,
  onSearchClear,
  onRefresh,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyBtQlR_uF7q-S8TQblCzLw7Fy7BvyIeXAg",
    libraries: ["places"],
  });

  const mapContainerStyle = {
    width: "100%",
    height: "400px",
    borderRadius: "8px",
    border: "1px solid #ced4da",
  };

  const defaultCenter = {
    lat: 51.5074,
    lng: -0.1276,
  };

  const navigate = useNavigate();
 const searchColumnOptions = [
  { value: "all", label: "All Columns" },
  { value: "id", label: "ID" },
  { value: "invoice_number", label: "Quote Number" },
  { value: "order_number", label: "Order Number" },
  { value: "customer_name", label: "Customer Name" },
  { value: "phone", label: "Phone" },
  { value: "address", label: "Customer Address" },
  { value: "job_site_address", label: "Job Site Address" },
];

const hasSearchValue =
  String(searchTerm || "").trim() !== "" || searchColumn !== "all";
  const [postCode, setPostCode] = useState("");
  const [jobDateSet, setJobDateSet] = useState(
    new Date().toISOString().slice(0, 16)
  );

  const [currentUser, setCurrentUser] = useState({});
  const [electricians, setElectricians] = useState([]);
  const [createJob, setCreateJob] = useState(false);
  const [selectedElectricians, setSelectedElectricians] = useState([]);
  const [selectedWorkforceIds, setSelectedWorkforceIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalQuoteId, setModalQuoteId] = useState(null);
  const [enableGeofencing, setEnableGeofencing] = useState(false);
  const [radius, setRadius] = useState(100);
  const [isJobLocationCustomerAddress, setIsJobLocationCustomerAddress] =
    useState(true);
  const [jobLocation, setJobLocation] = useState("");
  const [jobLocationSuggestions, setJobLocationSuggestions] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);

  const jobDescriptionQuillRef = useRef(null);
  const jobDescriptionQuillInstance = useRef(null);
  const jobLocationInputRef = useRef(null);

  const { page = 1, limit = 25, total = 0, totalPages = 1 } = pagination || {};

  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * limit, total);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const windowSize = 1;

    const addPage = (p) => pages.push(p);
    const addEllipsis = () => pages.push("...");

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) addPage(i);
      return pages;
    }

    addPage(1);

    if (page - windowSize > 2) {
      addEllipsis();
    }

    const start = Math.max(2, page - windowSize);
    const end = Math.min(totalPages - 1, page + windowSize);

    for (let i = start; i <= end; i++) {
      addPage(i);
    }

    if (page + windowSize < totalPages - 1) {
      addEllipsis();
    }

    addPage(totalPages);

    return pages;
  }, [page, totalPages]);

  const formatMoney = (amount) => {
    return Number(amount || 0).toFixed(2);
  };

  const fetchCoordinates = async (address) => {
    if (!address) {
      return {
        latitude: "",
        longitude: "",
      };
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          address
        )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=GB&limit=1`
      );

      const data = await response.json();

      if (data.features?.length > 0) {
        const [longitude, latitude] = data.features[0].geometry.coordinates;

        return {
          latitude,
          longitude,
        };
      }

      return {
        latitude: "",
        longitude: "",
      };
    } catch (error) {
      console.error("Error fetching coordinates:", error);

      return {
        latitude: "",
        longitude: "",
      };
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user) {
      navigate("/sign-in");
      return;
    }

    setCurrentUser(user);

    const fetchElectricians = async () => {
      try {
        const response = await fetch(`${API_URL}/api/user/fetch-electrician`);
        const data = await response.json();

        const formatted = data.data.map((e) => ({
          label: e.name,
          value: e.id,
        }));

        setElectricians(formatted);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch electricians",
        });
      }
    };

    fetchElectricians();
  }, [navigate]);

  const electricianSelectOptions = useMemo(() => {
    return electricians.filter((opt) => {
      const selectedInWorkforce = selectedWorkforceIds.some(
        (w) => w.value === opt.value
      );

      const selectedInElectrician = selectedElectricians.some(
        (e) => e.value === opt.value
      );

      if (selectedInElectrician) return true;

      return !selectedInWorkforce;
    });
  }, [electricians, selectedElectricians, selectedWorkforceIds]);

  const workforceSelectOptions = useMemo(() => {
    return electricians.filter((opt) => {
      const selectedInElectrician = selectedElectricians.some(
        (e) => e.value === opt.value
      );

      const selectedInWorkforce = selectedWorkforceIds.some(
        (w) => w.value === opt.value
      );

      if (selectedInWorkforce) return true;

      return !selectedInElectrician;
    });
  }, [electricians, selectedElectricians, selectedWorkforceIds]);

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete quote?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const response = await fetch(`${API_URL}/api/invoice/delete-invoice/${id}`, {
        method: "DELETE",
      });

      const data = await response.json();

      if (data.message === "Invoice deleted successfully") {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Quote deleted successfully!",
        });

        if (onRefresh) {
          onRefresh();
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data.message || "Failed to delete quote",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete quote: " + error.message,
      });
    }
  };

  const handleCreateJob = async () => {
    try {
      const quote = jobsData.find((job) => job.id === modalQuoteId);

      if (!quote) throw new Error("Quote not found");

      const safeMarker = markerPosition || {
        lat: "",
        lng: "",
      };

      const formData = new FormData();

      formData.append("client_name", quote.customer_name);
      formData.append("phone", quote.phone);
      formData.append("device_Type", "N/A");
      formData.append("job_cost", quote.amount);
      formData.append(
        "address",
        isJobLocationCustomerAddress ? selectedQuote?.address : jobLocation
      );
      formData.append("postcode", postCode || quote.postcode || "");
      formData.append("map_location", `${safeMarker.lat},${safeMarker.lng}`);
      formData.append("job_posted_by", currentUser?.id || "");
      formData.append("service_required", quote.service_required || "General");
      formData.append(
        "job_posted_to",
        selectedElectricians.map((e) => e.value).join(",")
      );
      formData.append("assigned_by", currentUser?.id || "");
      formData.append(
        "description",
        jobDescriptionQuillInstance.current?.root.innerHTML || ""
      );
      formData.append(
        "workforce_ids",
        selectedWorkforceIds.map((w) => w.value).join(",")
      );
      formData.append("enableGeofencing", enableGeofencing);
      formData.append("radius", radius);
      formData.append("quoteId", modalQuoteId);
      formData.append("referenceNo", errors.referenceNo || "");
      formData.append("job_date_set", jobDateSet);

      if (errors.attachments && errors.attachments.length > 0) {
        for (const file of errors.attachments) {
          formData.append("attachments", file);
        }
      }

      const response = await fetch(`${API_URL}/api/job/create-for-quote`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to create job");
      }
    } catch (err) {
      console.error("Error creating job:", err);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });

      throw err;
    }
  };

  useEffect(() => {
    if (
      createJob &&
      jobDescriptionQuillRef.current &&
      !jobDescriptionQuillInstance.current
    ) {
      jobDescriptionQuillInstance.current = new Quill(
        jobDescriptionQuillRef.current,
        {
          theme: "snow",
          placeholder: "Enter job description...",
        }
      );
    }
  }, [createJob]);

  useEffect(() => {
    const updateMap = async () => {
      const address = isJobLocationCustomerAddress
        ? selectedQuote?.address
        : jobLocation;

      if (!address) return;

      const coords = await fetchCoordinates(address);

      if (coords.latitude && coords.longitude) {
        setMarkerPosition({
          lat: coords.latitude,
          lng: coords.longitude,
        });

        setMapCenter({
          lat: coords.latitude,
          lng: coords.longitude,
        });
      }
    };

    updateMap();
  }, [isJobLocationCustomerAddress, jobLocation, selectedQuote]);

  const handleMakeIt = (id) => {
    const quote = jobsData.find((job) => job.id === id);

    if (!quote) return;

    setSelectedQuote(quote);
    setModalQuoteId(id);
    setCreateJob(false);
    setErrors({});
    setShowModal(true);
  };

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (jobLocation && !isJobLocationCustomerAddress) {
        try {
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
              jobLocation
            )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=GB&limit=5`
          );

          const data = await response.json();

          if (data.features) {
            setJobLocationSuggestions(data.features);
          } else {
            setJobLocationSuggestions([]);
          }
        } catch (error) {
          console.error("Error fetching suggestions:", error);
        }
      } else {
        setJobLocationSuggestions([]);
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [jobLocation, isJobLocationCustomerAddress]);

  const handleSubmit = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/invoice/convert-to-invoice/${modalQuoteId}`,
        {
          method: "PUT",
        }
      );

      await response.json();

      if (createJob) {
        if (!selectedElectricians || selectedElectricians.length === 0) {
          Swal.fire({
            icon: "warning",
            title: "Missing Field",
            text: "Please assign at least one electrician before creating a job.",
          });

          return;
        }

        await handleCreateJob();

        Swal.fire({
          icon: "success",
          title: "Job Created",
          text: "Job created and quote converted to invoice successfully.",
        }).then(() => {
          setShowModal(false);

          if (onRefresh) {
            onRefresh();
          }
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Invoice Created",
          text: "Quote converted to invoice successfully.",
        }).then(() => {
          setShowModal(false);

          if (onRefresh) {
            onRefresh();
          }
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
      });
    }
  };

  const TooltipButton = ({ tooltip, className, icon, onClick }) => {
    return (
      <OverlayTrigger
        placement="top"
        delay={{
          show: 180,
          hide: 80,
        }}
        overlay={
          <Tooltip id={`quote-tooltip-${tooltip.replace(/\s+/g, "-")}`}>
            {tooltip}
          </Tooltip>
        }
      >
        <span className="quote-action-tooltip-wrap">
          <button
            type="button"
            className={`quote-action-btn ${className}`}
            aria-label={tooltip}
            onClick={onClick}
          >
            <Icon icon={icon} />
          </button>
        </span>
      </OverlayTrigger>
    );
  };

  const renderActions = (quote) => {
    return (
      <div className="quote-action-bar">
        <TooltipButton
          tooltip="Make it Invoice"
          className="convert"
          icon="ph:arrows-clockwise"
          onClick={() => handleMakeIt(quote.id)}
        />

        <TooltipButton
          tooltip="View Quote"
          className="view"
          icon="ph:eye"
          onClick={() => navigate(`/quotes/preview/${quote.id}`)}
        />

        <TooltipButton
          tooltip="Edit Quote"
          className="edit"
          icon="ph:pencil-simple-line"
          onClick={() => navigate(`/quotes/edit/${quote.id}`)}
        />

        <TooltipButton
          tooltip="Delete Quote"
          className="delete"
          icon="ph:trash"
          onClick={() => handleDelete(quote.id)}
        />
      </div>
    );
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter" && onSearchSubmit) {
      onSearchSubmit();
    }
  };

  return (
    <div className="card basic-data-table custom-quote-table-card">
      <style>
        {`
        .quote-column-filter {
  min-height: 42px;
  width: 220px;
  border-radius: 10px;
  border: 1px solid #dbe4f0;
  font-weight: 700;
  color: #334155;
  box-shadow: none;
}
          .custom-quote-table-card {
            border: 1px solid #e8eef7;
            border-radius: 18px;
            overflow: hidden;
            box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
          }

          .custom-quote-table-card .card-header {
            border-bottom: 1px solid #edf2f7;
            background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
            padding: 18px 20px;
          }

          .quote-table-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 12px;
          }

        .quote-search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  width: min(100%, 820px);
}

          .quote-search-box input {
            min-height: 42px;
          }
            .quote-column-filter {
  min-height: 42px;
  width: 220px;
  border-radius: 10px;
  border: 1px solid #dbe4f0;
  font-weight: 700;
  color: #334155;
  box-shadow: none;
}

.quote-clear-btn {
  min-height: 42px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0 16px;
  background: #ffffff;
  color: #475569;
  font-weight: 700;
  cursor: pointer;
}

.quote-clear-btn:hover:not(:disabled) {
  background: #f8fafc;
  color: #0f172a;
}

.quote-clear-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.quote-clear-btn {
  min-height: 42px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0 16px;
  background: #ffffff;
  color: #475569;
  font-weight: 700;
  cursor: pointer;
}

.quote-clear-btn:hover:not(:disabled) {
  background: #f8fafc;
  color: #0f172a;
}

.quote-clear-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
          .quote-search-btn {
            min-height: 42px;
            border: 0;
            border-radius: 10px;
            padding: 0 16px;
            background: #2563eb;
            color: #ffffff;
            font-weight: 700;
            cursor: pointer;
          }

          .quote-table-scroll {
            width: 100%;
            overflow-x: auto;
            border: 1px solid #e8eef7;
            border-radius: 14px;
          }

          .custom-quote-table {
            width: 100%;
            min-width: 980px;
            margin: 0;
            border-collapse: separate;
            border-spacing: 0;
          }

          .custom-quote-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background: #f8fafc;
            color: #334155;
            font-size: 12px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            border-bottom: 1px solid #e2e8f0;
            padding: 13px 14px;
            white-space: nowrap;
          }

          .custom-quote-table tbody td {
            padding: 13px 14px;
            border-bottom: 1px solid rgba(226, 232, 240, 0.8);
            vertical-align: middle;
            font-size: 14px;
            color: #334155;
          }

          .custom-quote-table tbody tr:last-child td {
            border-bottom: none;
          }

          .custom-quote-row {
            transition: transform 0.15s ease, box-shadow 0.15s ease;
          }

          .custom-quote-row:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
          }

          .quote-id-link {
            border: 0;
            background: transparent;
            padding: 0;
            color: #2563eb;
            font-weight: 800;
            text-decoration: underline;
            cursor: pointer;
          }

          .quote-customer-cell {
            display: inline-block;
            max-width: 260px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-weight: 700;
            color: #0f172a;
          }

          .quote-muted-cell {
            color: #64748b;
          }

          .quote-amount-cell {
            font-weight: 800;
            color: #0f172a;
          }

          .quote-action-bar {
            display: inline-flex;
            align-items: center;
            justify-content: flex-end;
            gap: 6px;
            white-space: nowrap;
          }

          .quote-action-tooltip-wrap {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            line-height: 0;
          }

          .quote-action-btn {
            width: 32px;
            height: 32px;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            background: #ffffff;
            color: #334155 !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 17px;
            cursor: pointer;
            transition: all 0.15s ease;
            box-shadow: 0 4px 10px rgba(15, 23, 42, 0.06);
          }

          .quote-action-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 16px rgba(15, 23, 42, 0.12);
          }

          .quote-action-btn.convert {
            background: #ecfdf5;
            border-color: #bbf7d0;
            color: #059669 !important;
          }

          .quote-action-btn.view {
            background: #eff6ff;
            border-color: #bfdbfe;
            color: #2563eb !important;
          }

          .quote-action-btn.edit {
            background: #f5f3ff;
            border-color: #ddd6fe;
            color: #7c3aed !important;
          }

          .quote-action-btn.delete {
            background: #fef2f2;
            border-color: #fecaca;
            color: #dc2626 !important;
          }

          .custom-quote-mobile-list {
            display: none;
          }

          .custom-quote-card {
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            padding: 14px;
            margin-bottom: 12px;
            background: #ffffff;
          }

          .quote-mobile-top {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            align-items: flex-start;
            margin-bottom: 10px;
          }

          .quote-mobile-title {
            font-weight: 800;
            color: #0f172a;
            font-size: 15px;
            margin-top: 4px;
          }

          .quote-mobile-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
            margin-top: 12px;
          }

          .quote-mobile-label {
            display: block;
            font-size: 11px;
            color: #64748b;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            margin-bottom: 3px;
          }

          .quote-mobile-value {
            font-size: 13px;
            color: #334155;
            word-break: break-word;
          }

          .quote-mobile-actions {
            margin-top: 12px;
            display: flex;
            justify-content: flex-end;
          }

          .custom-pagination-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 12px;
            padding: 16px 0 0;
            border-top: 1px solid #e9ecef;
            margin-top: 16px;
          }

          .custom-pagination-footer .rows-per-page {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .custom-pagination-footer select {
            width: auto;
            padding: 4px 28px 4px 10px;
          }

          .custom-pagination-footer .pagination-info {
            color: #6c757d;
            font-size: 0.85rem;
            white-space: nowrap;
          }

          .custom-pagination-footer .pagination-nav {
            display: flex;
            align-items: center;
            gap: 4px;
            flex-wrap: wrap;
          }

          .custom-pagination-footer .page-btn {
            min-width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #dee2e6;
            background: #fff;
            border-radius: 6px;
            font-size: 0.85rem;
            cursor: pointer;
            color: #343a40;
            padding: 0 8px;
          }

          .custom-pagination-footer .page-btn:hover:not(:disabled) {
            background: #f1f3f5;
          }

          .custom-pagination-footer .page-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
          }

          .custom-pagination-footer .page-btn.active {
            background: #007bff;
            border-color: #007bff;
            color: #fff;
            font-weight: 600;
          }

          .custom-pagination-footer .page-ellipsis {
            padding: 0 4px;
            color: #adb5bd;
          }

          .tooltip {
            font-size: 12px;
            font-weight: 600;
          }

          .tooltip-inner {
            border-radius: 8px;
            padding: 6px 10px;
          }

          @media (max-width: 920px) {
            .quote-table-scroll {
              display: none;
            }
        .quote-column-filter {
  width: 100%;
}

.quote-clear-btn,
.quote-search-btn {
  width: 100%;
}

            .custom-quote-mobile-list {
              display: block;
            }

            .custom-pagination-footer {
              align-items: stretch;
              flex-direction: column;
            }

            .custom-pagination-footer .pagination-nav {
              justify-content: center;
            }
          }

          @media (max-width: 640px) {
            .custom-quote-table-card .card-header,
            .custom-quote-table-card .card-body {
              padding-left: 12px;
              padding-right: 12px;
            }

            .quote-table-toolbar {
              align-items: stretch;
              flex-direction: column;
            }

            .quote-search-box {
              width: 100%;
              flex-direction: column;
              align-items: stretch;
            }

            .quote-mobile-grid {
              grid-template-columns: 1fr;
            }

            .custom-pagination-footer .rows-per-page {
              justify-content: space-between;
            }

            .custom-pagination-footer .pagination-info {
              white-space: normal;
              text-align: center;
            }
          }
        `}
      </style>

      <div className="card-header">
        <div className="quote-table-toolbar">
          <div>
            <h5 className="card-title mb-0">Quotes</h5>
          </div>

          <Link className="btn btn-info" to="/create-quote">
            Add New
          </Link>
        </div>

        <div className="quote-search-box mt-3">
  <select
    className="form-select quote-column-filter"
    value={searchColumn}
    onChange={(e) =>
      onSearchColumnChange && onSearchColumnChange(e.target.value)
    }
    disabled={loading}
  >
    {searchColumnOptions.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>

  <input
    type="text"
    className="form-control"
    placeholder={
      searchColumn === "all"
        ? "Search by ID, quote number, order number, name, phone or address..."
        : `Search by ${
            searchColumnOptions.find((item) => item.value === searchColumn)
              ?.label || "selected column"
          }...`
    }
    value={searchTerm}
    onChange={(e) =>
      onSearchTermChange && onSearchTermChange(e.target.value)
    }
    onKeyDown={handleSearchKeyDown}
  />

  <button
    type="button"
    className="quote-clear-btn"
    onClick={onSearchClear}
    disabled={loading || !hasSearchValue}
  >
    Clear
  </button>

  <button
    type="button"
    className="quote-search-btn"
    onClick={onSearchSubmit}
    disabled={loading}
  >
    Search
  </button>
</div>
      </div>

      <div className="card-body">
        <div className="quote-table-scroll">
          <table className="custom-quote-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Customer Name</th>
                <th>Customer Phone</th>
                <th>Issued Date</th>
                <th>Amount</th>
                <th>Action</th>
              </tr>
            </thead>

          <tbody>
  {jobsData.length === 0 ? (
    <tr>
      <td colSpan={6} className="text-center py-4 text-muted">
        No Quotes Found
      </td>
    </tr>
  ) : (
    jobsData.map((quote) => (
      <tr key={quote.id} className="custom-quote-row">
        <td>
          <button
            type="button"
            className="quote-id-link"
            onClick={() => navigate(`/quotes/preview/${quote.id}`)}
          >
            {quote.id}
          </button>
        </td>

        <td>
          <span
            className="quote-customer-cell"
            title={quote.customer_name}
          >
            {quote.customer_name || "N/A"}
          </span>
        </td>

        <td>
          <span className="quote-muted-cell">
            {quote.phone || "N/A"}
          </span>
        </td>

        <td>
          <span className="quote-muted-cell">
            {formatDateString(quote.issue_date)}
          </span>
        </td>

        <td>
          <span className="quote-amount-cell">
            £{formatMoney(quote.amount)}
          </span>
        </td>

        <td>{renderActions(quote)}</td>
      </tr>
    ))
  )}
</tbody>
          </table>
        </div>

      <div className="custom-quote-mobile-list">
  {jobsData.length === 0 ? (
    <div className="custom-quote-card text-center text-muted">
      No Quotes Found
    </div>
  ) : (
    jobsData.map((quote) => (
      <div key={quote.id} className="custom-quote-card">
        <div className="quote-mobile-top">
          <div>
            <button
              type="button"
              className="quote-id-link"
              onClick={() => navigate(`/quotes/preview/${quote.id}`)}
            >
              #{quote.id}
            </button>

            <div className="quote-mobile-title">
              {quote.customer_name || "N/A"}
            </div>
          </div>

          <span className="badge bg-info">Quote</span>
        </div>

        <div className="quote-mobile-grid">
          <div>
            <span className="quote-mobile-label">Phone</span>
            <span className="quote-mobile-value">
              {quote.phone || "N/A"}
            </span>
          </div>

          <div>
            <span className="quote-mobile-label">Issue Date</span>
            <span className="quote-mobile-value">
              {formatDateString(quote.issue_date)}
            </span>
          </div>

          <div>
            <span className="quote-mobile-label">Amount</span>
            <span className="quote-mobile-value">
              £{formatMoney(quote.amount)}
            </span>
          </div>
        </div>

        <div className="quote-mobile-actions">{renderActions(quote)}</div>
      </div>
    ))
  )}
</div>
        <div className="custom-pagination-footer">
          <div className="rows-per-page">
            <span className="pagination-info">Rows per page:</span>

            <select
              className="form-select form-select-sm"
              value={limit}
              onChange={(e) =>
                onLimitChange && onLimitChange(Number(e.target.value))
              }
              disabled={loading}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <span className="pagination-info">
            {total === 0
              ? "No entries"
              : `Showing ${rangeStart}-${rangeEnd} of ${total}`}
          </span>

          <div className="pagination-nav">
            <button
              type="button"
              className="page-btn"
              onClick={() => onPageChange && onPageChange(1)}
              disabled={page <= 1 || loading}
              title="First page"
            >
              <Icon icon="bi:chevron-double-left" />
            </button>

            <button
              type="button"
              className="page-btn"
              onClick={() => onPageChange && onPageChange(page - 1)}
              disabled={page <= 1 || loading}
              title="Previous page"
            >
              <Icon icon="bi:chevron-left" />
            </button>

            {pageNumbers.map((p, index) =>
              p === "..." ? (
                <span key={`ellipsis-${index}`} className="page-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={`page-btn ${p === page ? "active" : ""}`}
                  onClick={() => onPageChange && onPageChange(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              )
            )}

            <button
              type="button"
              className="page-btn"
              onClick={() => onPageChange && onPageChange(page + 1)}
              disabled={page >= totalPages || loading}
              title="Next page"
            >
              <Icon icon="bi:chevron-right" />
            </button>

            <button
              type="button"
              className="page-btn"
              onClick={() => onPageChange && onPageChange(totalPages)}
              disabled={page >= totalPages || loading}
              title="Last page"
            >
              <Icon icon="bi:chevron-double-right" />
            </button>
          </div>
        </div>
      </div>

      <Modal
        size="lg"
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="h6">Convert Quote to Invoice</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="createJobCheck"
              checked={createJob}
              onChange={(e) => setCreateJob(e.target.checked)}
            />

            <label className="form-check-label" htmlFor="createJobCheck">
              Also create a Job for this Quote
            </label>
          </div>

          {!createJob ? (
            <p>Are you sure you want to convert this quote into an invoice?</p>
          ) : (
            <>
              <div className="mb-3">
                <label>Job Reference No</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Job Reference No"
                  value={errors.referenceNo || ""}
                  onChange={(e) =>
                    setErrors({
                      ...errors,
                      referenceNo: e.target.value,
                    })
                  }
                />
              </div>

              <div className="mb-3">
                <label>
                  Assign Electricians <span className="text-danger">*</span>
                </label>

                <Select
                  isMulti
                  options={electricianSelectOptions}
                  value={selectedElectricians}
                  onChange={(e) => setSelectedElectricians(e || [])}
                  placeholder="Select Electricians"
                />
              </div>

              <div className="mb-3">
                <label>Assign Workforce</label>

                <Select
                  isMulti
                  options={workforceSelectOptions}
                  value={selectedWorkforceIds}
                  onChange={(vals) => setSelectedWorkforceIds(vals || [])}
                  placeholder="Select Workforce"
                />
              </div>

              <div className="mb-3">
                <label>Job Date</label>

                <input
                  type="datetime-local"
                  className="form-control"
                  value={jobDateSet}
                  onChange={(e) => setJobDateSet(e.target.value)}
                />
              </div>

              <div className="mb-3 d-flex align-items-center gap-2">
                <label>Is Job Location same as Customer Address?</label>

                <Switch
                  onChange={() =>
                    setIsJobLocationCustomerAddress(
                      !isJobLocationCustomerAddress
                    )
                  }
                  checked={isJobLocationCustomerAddress}
                />
              </div>

              {!isJobLocationCustomerAddress && (
                <>
                  <div className="mb-3 position-relative">
                    <label>Job Location</label>

                    <input
                      ref={jobLocationInputRef}
                      type="text"
                      className="form-control"
                      value={jobLocation}
                      onChange={(e) => setJobLocation(e.target.value)}
                      placeholder="Search job location..."
                    />

                    {jobLocationSuggestions.length > 0 && (
                      <ul
                        className="list-group position-absolute w-100 mt-1 shadow"
                        style={{
                          zIndex: 1050,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {jobLocationSuggestions.map((place, index) => (
                          <li
                            key={index}
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              setJobLocation(place.place_name);

                              const coords = {
                                latitude: place.geometry.coordinates[1],
                                longitude: place.geometry.coordinates[0],
                              };

                              setMarkerPosition({
                                lat: coords.latitude,
                                lng: coords.longitude,
                              });

                              setMapCenter({
                                lat: coords.latitude,
                                lng: coords.longitude,
                              });

                              setJobLocationSuggestions([]);
                            }}
                          >
                            {place.place_name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="mb-3">
                    <label>Postcode</label>

                    <input
                      type="text"
                      className="form-control"
                      value={postCode}
                      onChange={(e) => setPostCode(e.target.value)}
                      placeholder="Enter postcode"
                    />
                  </div>
                </>
              )}

              <div className="mb-3">
                <label>Attachments</label>

                <input
                  type="file"
                  multiple
                  className="form-control"
                  onChange={(e) =>
                    setErrors({
                      ...errors,
                      attachments: e.target.files,
                    })
                  }
                />
              </div>

              <div className="mb-3">
                <label>Job Description</label>
                <div ref={jobDescriptionQuillRef} />
              </div>

              <div className="mb-3 d-flex align-items-center gap-2">
                <label>Enable Geofencing</label>

                <Switch
                  onChange={() => setEnableGeofencing(!enableGeofencing)}
                  checked={enableGeofencing}
                />

                {enableGeofencing && (
                  <input
                    type="number"
                    className="form-control w-auto"
                    value={radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                    placeholder="Radius (m)"
                  />
                )}
              </div>

              {loadError && (
                <div className="alert alert-danger">
                  Error loading Google Maps. Please try again later.
                </div>
              )}

              {isLoaded && (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={13}
                >
                  {markerPosition && <Marker position={markerPosition} />}

                  {enableGeofencing && markerPosition && (
                    <Circle
                      center={markerPosition}
                      radius={radius}
                      options={{
                        strokeColor: "#FF0000",
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: "#FF0000",
                        fillOpacity: 0.35,
                      }}
                    />
                  )}
                </GoogleMap>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>

          <Button variant="primary" onClick={handleSubmit}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuoteListLayer;